import { useT } from '@transifex/react';
import type {
	ReactNode,
	InputHTMLAttributes,
	ChangeEvent,
	SyntheticEvent,
} from 'react';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import TextAreaAutosize from 'react-textarea-autosize';

import { debounce } from 'pkg/timings';
import withForwardedRef from 'pkg/withForwardedRef';

import type { InputChangeEvent } from 'components/form/inputs';
import InputBase from 'components/form/inputs/input_base';

import * as css from './styles.css';

interface Props extends Omit<
	InputHTMLAttributes<HTMLTextAreaElement>,
	'onChange' | 'style'
> {
	minRows?: number;
	maxRows?: number;
	small?: boolean;
	transparent?: boolean;
	borderless?: boolean;
	changeDelay?: number;
	forwardedRef?: any;
	testid?: string;
	onChange?: InputChangeEvent;
	onTyping?: InputChangeEvent;
	className?: string;
	children?: ReactNode;
}

/**
 * Textarea component
 *
 * A dynamic textarea input that automatically adjusts its row height based on content.
 */
export const Textarea = withForwardedRef(
	({
		minRows = 1,
		maxRows,
		changeDelay,
		small,
		transparent,
		borderless,
		forwardedRef,
		testid,
		onChange,
		onTyping,
		className,
		children,
		...rest
	}: Props) => {
		const methods = useFormContext();
		const t = useT();

		const validationRules =
			rest.name &&
			methods &&
			methods.register(rest.name, {
				required: rest.required,
				validate: (value: string | number) => {
					if (typeof value === 'string' && rest.required) {
						if (value.trim() === '') {
							return t('This field cannot be blank');
						}
					}

					return true;
				},
			});

		const emitChange = useMemo(
			() =>
				debounce((event: ChangeEvent<HTMLTextAreaElement> | SyntheticEvent) => {
					if (validationRules) {
						validationRules.onChange(event);
					}
					onChange(event);
				}, changeDelay),
			[changeDelay, onChange]
		);

		let changeHandler: InputChangeEvent;
		let inputHandler: InputChangeEvent;

		if (onChange && !changeDelay) {
			changeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
				if (validationRules) {
					validationRules.onChange(event);
				}

				onChange(event);
			};
		} else if (onChange && changeDelay) {
			inputHandler = (event: SyntheticEvent) => {
				if (onTyping) {
					onTyping(event);
				}

				emitChange(event);
			};
		} else if (!onChange && validationRules) {
			changeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
				validationRules.onChange(event);
			};
		}

		let ref = forwardedRef;

		if (forwardedRef && validationRules) {
			ref = (e: HTMLInputElement) => {
				validationRules.ref(e);
				forwardedRef.current = e; // you can still assign to ref
			};
		} else if (validationRules) {
			ref = validationRules.ref;
		}

		return (
			<InputBase
				small={small}
				transparent={transparent}
				borderless={borderless}
				textarea
				className={className}
				disabled={rest.disabled}>
				{children}
				<TextAreaAutosize
					ref={ref}
					className={css.textarea}
					onChange={changeHandler}
					onInput={inputHandler}
					data-testid={testid}
					minRows={minRows}
					maxRows={maxRows}
					{...rest}
				/>
			</InputBase>
		);
	}
);
